import type { Course } from '../types';
import { getImageUrlWithFallback } from './imageUtils';
import { getCoursePathSegment } from './coursePath';

/** کلید فیلتر تب دسته؛ برای دورهٔ بدون دسته از `_none` استفاده می‌شود */
export const HOME_CAROUSEL_UNCATEGORIZED_KEY = '_none';

const UNCATEGORIZED_TAB_LABEL = 'بدون دسته‌بندی';

export type HomeCarouselCourseCard = {
  id: string;
  pathSegment: string;
  title: string;
  thumbnail: string;
  lessonsCount: number;
  price: number;
  oldPrice: number;
  discount: number;
  rating: number;
  instructor: string;
  duration: string;
  categoryFilterKey: string;
  /** برچسب تب دسته (نام دسته از API) */
  categoryTabLabel: string;
};

function parseRatingFromSatisfactionLabel(label: string | null | undefined): number {
  if (!label || typeof label !== 'string') return 0;
  const m = label.replace(/[٫،]/g, '.').match(/(\d+(\.\d+)?)/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  return Number.isFinite(n) ? Math.min(5, Math.max(0, n)) : 0;
}

function formatDurationFa(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '—';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h.toLocaleString('fa-IR')} ساعت`;
  if (h > 0) return `${h.toLocaleString('fa-IR')} ساعت`;
  return `${m.toLocaleString('fa-IR')} دقیقه`;
}

function lessonCount(course: Course): number {
  const v = course.videos?.length ?? 0;
  const a = course.audios?.length ?? 0;
  return v + a;
}

function totalDurationSeconds(course: Course): number {
  let s = 0;
  for (const x of course.videos ?? []) {
    const d = x.duration;
    if (typeof d === 'number' && d > 0) s += d;
  }
  for (const x of course.audios ?? []) {
    const d = x.duration;
    if (typeof d === 'number' && d > 0) s += d;
  }
  return s;
}

/**
 * نگاشت دورهٔ API (پس از process در بک‌اند) به شکل کارت کاروسل خانه.
 */
export function mapCourseToHomeCarouselCard(course: Course): HomeCarouselCourseCard {
  const listPrice = Number(
    course.listPrice != null ? course.listPrice : course.price ?? 0,
  );
  const effective = Number(
    course.effectivePrice != null ? course.effectivePrice : course.price ?? 0,
  );
  const hasDisc = Boolean(course.hasActiveDiscount) && listPrice > effective && effective >= 0;
  const discountPct =
    hasDisc && listPrice > 0 ? Math.min(100, Math.round(((listPrice - effective) / listPrice) * 100)) : 0;

  const slug = course.category?.slug?.trim();
  const categoryFilterKey = slug && slug.length > 0 ? slug : HOME_CAROUSEL_UNCATEGORIZED_KEY;
  const categoryTabLabel =
    categoryFilterKey === HOME_CAROUSEL_UNCATEGORIZED_KEY
      ? UNCATEGORIZED_TAB_LABEL
      : (course.category?.name?.trim() || slug || categoryFilterKey);

  const thumb = getImageUrlWithFallback(course.thumbnail);

  return {
    id: course.id,
    pathSegment: getCoursePathSegment(course),
    title: course.title,
    thumbnail: thumb,
    lessonsCount: lessonCount(course),
    price: effective,
    oldPrice: hasDisc ? listPrice : 0,
    discount: discountPct,
    rating: parseRatingFromSatisfactionLabel(course.satisfactionAvgLabel) || 0,
    instructor: 'تیم آموزشی',
    duration: formatDurationFa(totalDurationSeconds(course)),
    categoryFilterKey,
    categoryTabLabel,
  };
}

export type HomeCarouselCategoryTab = { key: string; label: string };

/**
 * تب‌های دسته از روی کارت‌های همان کاروسل (فقط دسته‌هایی که در لیست هستند).
 */
export function buildHomeCarouselCategoryTabs(cards: HomeCarouselCourseCard[]): HomeCarouselCategoryTab[] {
  const labelByKey = new Map<string, string>();
  for (const c of cards) {
    if (!labelByKey.has(c.categoryFilterKey)) {
      labelByKey.set(c.categoryFilterKey, c.categoryTabLabel);
    }
  }
  const entries = Array.from(labelByKey.entries()).filter(
    ([k]) => k !== HOME_CAROUSEL_UNCATEGORIZED_KEY,
  );
  entries.sort((a, b) => a[1].localeCompare(b[1], 'fa'));
  const tabs: HomeCarouselCategoryTab[] = [{ key: 'all', label: 'همه' }];
  entries.forEach(([key, label]) => {
    tabs.push({ key, label });
  });
  if (labelByKey.has(HOME_CAROUSEL_UNCATEGORIZED_KEY)) {
    tabs.push({ key: HOME_CAROUSEL_UNCATEGORIZED_KEY, label: UNCATEGORIZED_TAB_LABEL });
  }
  return tabs;
}

/** اگر تب فعال دیگر در لیست نیست (مثلاً بعد از فیلتر API)، به «همه» برگردان */
export function normalizeCarouselActiveTab(
  active: string,
  tabs: HomeCarouselCategoryTab[],
): string {
  if (tabs.some((t) => t.key === active)) return active;
  return 'all';
}
